import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useGetBuildingPaymentSettings, useUpdateBuildingPaymentSettings } from '@/api/building'
import { PmsProvider } from '@/api/pms'
import { FieldRow, FieldSkeleton, FieldsTable, FieldViewValue } from '@/components/layout/field-row'
import { TabActions, TabCard } from '@/components/layout/tab-card'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import { DirtyFieldChange } from '@/hooks'
import { Building } from '@/reducers/types'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../../../../shared/form-changes'
import {
  getPayRentDefaults,
  getPayRentPayload,
  makePayRentSchema,
  PAY_RENT_FEE_FIELDS,
  PAY_RENT_FEE_TYPES,
  PayRentFormData,
} from './pay-rent.utils'
import { PayRentFields } from './pay-rent-fields'

const SKELETON_LABEL_KEYS = [
  'organisation:places.integrations.payRentConfig.convenienceFee.card',
  'organisation:places.integrations.payRentConfig.convenienceFee.ach',
  'organisation:places.integrations.payRentConfig.dailyBatch.label',
  'organisation:places.integrations.payRentConfig.processingDelay.label',
  'organisation:places.integrations.payRentConfig.allowAutopay',
  'organisation:places.integrations.payRentConfig.allowChargesSelection',
  'organisation:places.integrations.payRentConfig.allowPartialPayment',
] as const

interface PayRentCardProps {
  building: Building
  onDirtyChange: (isDirty: boolean, getChanges?: () => DirtyFieldChange[]) => void
  provider: PmsProvider
}

export const PayRentCard = ({ building, onDirtyChange, provider }: PayRentCardProps) => {
  const { t } = useTranslation(['organisation'])
  const isRentManager = provider === 'rentManager'
  const { data, isError, isLoading } = useGetBuildingPaymentSettings(building.id)
  const { isPending, mutateAsync } = useUpdateBuildingPaymentSettings()
  const [isEditing, setIsEditing] = useState(false)

  const schema = useMemo(() => makePayRentSchema(t, isRentManager), [t, isRentManager])
  const serverValues = useMemo(() => (data ? getPayRentDefaults(data) : undefined), [data])

  const form = useForm<PayRentFormData>({
    defaultValues: serverValues,
    mode: 'onTouched',
    resetOptions: { keepDirtyValues: true },
    resolver: zodResolver(schema),
    values: serverValues,
  })
  const { isDirty } = form.formState
  const watched = useWatch({ control: form.control }) as PayRentFormData

  const changeFields = useMemo<FormChangeFields<PayRentFormData>>(() => {
    const onOff = (value: unknown) =>
      value ? t('organisation:places.save.on') : t('organisation:places.save.off')
    const fields: FormChangeFields<PayRentFormData> = {
      allowAutopay: {
        format: onOff,
        label: t('organisation:places.integrations.payRentConfig.allowAutopay'),
      },
      allowChargesSelection: {
        format: onOff,
        label: t('organisation:places.integrations.payRentConfig.allowChargesSelection'),
      },
      allowPartialPayment: {
        format: onOff,
        label: t('organisation:places.integrations.payRentConfig.allowPartialPayment'),
      },
      amenityChargeCode: {
        label: t('organisation:places.integrations.payRentConfig.amenityChargeCode.label'),
      },
      chargeCodesMappings: {
        items: (value) =>
          (value as PayRentFormData['chargeCodesMappings']).map(
            ({ code, description }) => `${code}: ${description}`,
          ),
        label: t('organisation:places.integrations.payRentConfig.chargeCodesMappings.title'),
      },
      dailyBatch: {
        format: (value) =>
          value
            ? t('organisation:places.integrations.payRentConfig.dailyBatch.batch')
            : t('organisation:places.integrations.payRentConfig.dailyBatch.individual'),
        label: t('organisation:places.integrations.payRentConfig.dailyBatch.label'),
      },
      prePaidChargeCode: {
        label: t('organisation:places.integrations.payRentConfig.prePaidChargeCode.label'),
      },
      processingDelay: {
        label: t('organisation:places.integrations.payRentConfig.processingDelay.label'),
      },
    }

    PAY_RENT_FEE_TYPES.forEach((type) => {
      const { flatAmount, percentage } = PAY_RENT_FEE_FIELDS[type]
      fields[percentage] = {
        label: t(`organisation:places.integrations.payRentConfig.convenienceFee.${type}Percentage`),
      }
      fields[flatAmount] = {
        label: t(`organisation:places.integrations.payRentConfig.convenienceFee.${type}FlatAmount`),
      }
    })

    return fields
  }, [t])

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  useEffect(() => {
    const isDirtyForm = isEditing && isDirty
    onDirtyChange(isDirtyForm, isDirtyForm ? getChanges : undefined)
  }, [isEditing, isDirty, getChanges, onDirtyChange])

  const handleCancel = () => {
    form.reset(serverValues)
    setIsEditing(false)
  }

  const handleSave = form.handleSubmit(async (values) => {
    if (!data) return

    try {
      const saved = await mutateAsync({
        buildingId: building.id,
        data: getPayRentPayload(values, data),
      })
      form.reset(getPayRentDefaults(saved))
      setIsEditing(false)
      toastFactory.success(t('organisation:places.integrations.payRentConfig.toasts.saved'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  })

  const renderContent = () => {
    if (isLoading) {
      return (
        <FieldsTable aria-hidden>
          {SKELETON_LABEL_KEYS.map((labelKey) => (
            <FieldRow key={labelKey} label={t(labelKey)}>
              <FieldViewValue>
                <FieldSkeleton height="1.6rem" width="20rem" />
              </FieldViewValue>
            </FieldRow>
          ))}
        </FieldsTable>
      )
    }

    if (isError || !serverValues) {
      return (
        <Typography as="p" color="black500" size="text-medium">
          {t('organisation:places.integrations.payRentConfig.toasts.fetchFailed')}
        </Typography>
      )
    }

    return (
      <PayRentFields
        isEditing={isEditing}
        isRentManager={isRentManager}
        values={isEditing ? { ...serverValues, ...watched } : serverValues}
      />
    )
  }

  return (
    <FormProvider {...form}>
      <TabCard
        actions={
          serverValues && (
            <TabActions
              isDirty={isDirty}
              isEditing={isEditing}
              isSaving={isPending}
              onCancel={handleCancel}
              onEdit={() => setIsEditing(true)}
              onSave={handleSave}
            />
          )
        }
        title={t('organisation:places.integrations.payRentConfig.title')}
      >
        {renderContent()}
      </TabCard>
    </FormProvider>
  )
}
